import React from 'react';

const OutdoorSkeletonLoader = () => {
  const skeletonStyle = {
    background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: '8px',
  };

  const Skeleton = ({ width, height, className = '', style = {} }) => (
    <div
      className={`skeleton ${className}`}
      style={{
        ...skeletonStyle,
        width,
        height,
        ...style,
      }}
    />
  );

  const CardSkeleton = () => (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Skeleton width="100%" height="200px" style={{ borderRadius: '0' }} />
      <div style={{ padding: '20px' }}>
        <Skeleton width="80%" height="20px" style={{ marginBottom: '12px' }} />
        <Skeleton width="100%" height="14px" style={{ marginBottom: '8px' }} />
        <Skeleton width="100%" height="14px" style={{ marginBottom: '8px' }} />
        <Skeleton width="60%" height="14px" />
      </div>
    </div>
  );

  const RegionCardSkeleton = () => (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Skeleton width="100%" height="180px" style={{ borderRadius: '0' }} />
      <div style={{ padding: '15px' }}>
        <Skeleton width="70%" height="18px" style={{ marginBottom: '10px' }} />
        <Skeleton width="100%" height="12px" style={{ marginBottom: '6px' }} />
        <Skeleton width="100%" height="12px" style={{ marginBottom: '6px' }} />
        <Skeleton width="80%" height="12px" />
      </div>
    </div>
  );

  const SectionHeader = ({ titleWidth = '300px' }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
      }}
    >
      <Skeleton width={titleWidth} height="32px" />
      <div style={{ display: 'flex', gap: '10px' }}>
        <Skeleton width="40px" height="40px" style={{ borderRadius: '50%' }} />
        <Skeleton width="40px" height="40px" style={{ borderRadius: '50%' }} />
      </div>
    </div>
  );

  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: '#f8f9fa',
        padding: '20px',
      }}
    >
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          
          @media (max-width: 1024px) {
            .four-column {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .links-grid {
              grid-template-columns: repeat(3, 1fr) !important;
            }
          }
          
          @media (max-width: 768px) {
            .two-column {
              grid-template-columns: 1fr !important;
            }
            .four-column {
              grid-template-columns: 1fr !important;
            }
            .links-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .section-header {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 15px !important;
            }
          }
          
          @media (max-width: 480px) {
            .links-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Hero Section */}
        <div style={{ marginBottom: '60px' }}>
          <Skeleton width="100%" height="120px" style={{ marginBottom: '20px' }} />
        </div>

        {/* Latest Outdoor Adventure Stories */}
        <div style={{ marginBottom: '50px' }}>
          <SectionHeader titleWidth="400px" />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '30px',
            }}
            className="cards-grid"
          >
            {[...Array(3)].map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        </div>

        {/* Read more from Cities/Stores */}
        <div style={{ marginBottom: '50px' }}>
          <SectionHeader titleWidth="350px" />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '30px',
            }}
            className="two-column"
          >
            {[...Array(6)].map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <Skeleton
          width="150px"
          height="45px"
          style={{
            borderRadius: '25px',
            margin: '30px auto',
            display: 'block',
          }}
        />

        {/* Find best Outdoor Activities by Region */}
        <div style={{ marginBottom: '50px' }}>
          <SectionHeader titleWidth="450px" />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '20px',
            }}
            className="four-column"
          >
            {[...Array(8)].map((_, i) => (
              <RegionCardSkeleton key={i} />
            ))}
          </div>
        </div>

        {/* Browse all activities */}
        <div style={{ marginTop: '40px' }}>
          <Skeleton width="250px" height="28px" style={{ marginBottom: '30px' }} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '30px 20px',
            }}
            className="links-grid"
          >
            {[...Array(32)].map((_, i) => (
              <Skeleton key={i} width="80%" height="16px" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutdoorSkeletonLoader;
